/**
 * Validações para Chaves PIX
 * Suporta: CPF, Telefone, E-mail
 */

// Imports de bibliotecas de validação robustas
// Nota: será necessário instalar as dependências no package.json
import { cpf as cpfValidator } from "cpf-cnpj-validator";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import * as validator from "validator";

// DDDs válidos do Brasil (2 dígitos) - usado como fallback extra
const VALID_DDDS = [
  11, 12, 13, 14, 15, 16, 17, 18, 19, // São Paulo
  21, 22, 24, // Rio de Janeiro
  27, 28, // Espírito Santo
  31, 32, 33, 34, 35, 37, 38, // Minas Gerais
  41, 42, 43, 44, 45, 46, // Paraná
  47, 48, 49, // Santa Catarina
  51, 53, 54, 55, // Rio Grande do Sul
  61, // Distrito Federal
  62, 64, // Goiás
  63, // Tocantins
  65, 66, // Mato Grosso
  67, // Mato Grosso do Sul
  68, // Acre
  69, // Rondônia
  71, 73, 74, 75, 77, // Bahia
  79, // Sergipe
  81, 87, // Pernambuco
  82, // Alagoas
  83, // Paraíba
  84, // Rio Grande do Norte
  85, 88, // Ceará
  86, 89, // Piauí
  91, 93, 94, // Pará
  92, 97, // Amazonas
  95, // Amapá
  96, // Roraima
  98, 99, // Maranhão
];

/**
 * Valida um CPF usando o algoritmo de dígito verificador
 * @param cpf - CPF com ou sem formatação (ex: "123.456.789-09" ou "12345678909")
 * @returns true se o CPF é válido, false caso contrário
 */
export const isValidCPF = (cpf: string): boolean => {
  const cleanCPF = cpf.replace(/\D/g, "");
  // Usa biblioteca (mais confiável e coberta por testes)
  if (cleanCPF.length === 11) {
    return cpfValidator.isValid(cleanCPF);
  }
  return false;
};

function nationalPhoneDigits(phone: string): string {
  const digits = (phone || "").replace(/\D/g, "");
  if (
    digits.startsWith("55") &&
    (digits.length === 12 || digits.length === 13)
  ) {
    return digits.slice(2);
  }
  return digits;
}

function hasValidBrazilianNumber(phone: string): boolean {
  const national = nationalPhoneDigits(phone);
  if (national.length !== 10 && national.length !== 11) return false;
  const ddd = parseInt(national.slice(0, 2), 10);
  if (!VALID_DDDS.includes(ddd)) return false;
  const numberPart = national.slice(2);
  if (numberPart.length === 9 && numberPart.charAt(0) !== "9") return false;
  return numberPart.length === 8 || numberPart.length === 9;
}

/**
 * Valida um número de telefone brasileiro com DDD
 * @param phone - Telefone com ou sem formatação (ex: "(11) 99999-9999" ou "11999999999")
 * @returns true se o telefone é válido, false caso contrário
 */
export const isValidPhone = (phone: string): boolean => {
  const raw = phone?.toString().trim();
  if (!raw || !hasValidBrazilianNumber(raw)) return false;
  const digits = raw.replace(/\D/g, "");
  const brPrefixed = raw.startsWith("+")
    ? raw
    : digits.startsWith("55") && (digits.length === 12 || digits.length === 13)
      ? `+${digits}`
      : `+55${digits}`;
  const parsed = parsePhoneNumberFromString(brPrefixed, "BR");
  if (parsed && parsed.isValid() && parsed.country === "BR") {
    return true;
  }
  return hasValidBrazilianNumber(raw);
};

/**
 * Valida um endereço de e-mail
 * @param email - E-mail a validar
 * @returns true se o e-mail é válido, false caso contrário
 */
export const isValidEmail = (email: string): boolean => {
  // Usa validator.js para validação robusta
  return validator.isEmail(email || "", { allow_utf8_local_part: true, require_tld: true }) === true;
};

/**
 * Valida uma chave PIX baseado no tipo
 * @param pixKey - Chave PIX a validar
 * @param pixType - Tipo da chave: "CPF", "Telefone", "E-mail"
 * @returns objeto com validação e mensagem de erro
 */
export const validatePixKey = (
  pixKey: string,
  pixType: string
): { isValid: boolean; error?: string } => {
  if (!pixKey || !pixType) {
    return { isValid: false, error: "Chave PIX e tipo são obrigatórios" };
  }

  const trimmedKey = pixKey.trim();

  switch (pixType) {
    case "CPF":
      if (!isValidCPF(trimmedKey)) {
        return { isValid: false, error: "CPF inválido" };
      }
      return { isValid: true };

    case "Telefone":
      if (!isValidPhone(trimmedKey)) {
        return { isValid: false, error: "Telefone inválido (DDD + número)" };
      }
      return { isValid: true };

    case "E-mail":
      if (!isValidEmail(trimmedKey)) {
        return { isValid: false, error: "E-mail inválido" };
      }
      return { isValid: true };

    default:
      return { isValid: false, error: "Tipo de chave PIX não suportado" };
  }
};

/**
 * Dígitos enquanto a pessoa digita. Telefone não recebe o 55 do país aqui:
 * 10 ou 11 dígitos são DDD + número; 12 ou 13 que já começam com 55 ficam para o blur.
 */
export const normalizePixKeyInput = (raw: string, pixType: string): string => {
  if (pixType === "CPF") {
    return raw.replace(/\D/g, "").slice(0, 11);
  }

  if (pixType === "Telefone") {
    return raw.replace(/\D/g, "").slice(0, 13);
  }

  if (pixType === "E-mail") {
    return raw.replace(/\s/g, "");
  }

  return raw;
};

/** Grava 55 + DDD + número. 10 ou 11 dígitos são nacionais, mesmo com DDD 55. */
export const canonicalizePixPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (
    (digits.length === 12 || digits.length === 13) &&
    digits.startsWith("55")
  ) {
    return digits;
  }
  if (digits.length === 10 || digits.length === 11) {
    return `55${digits}`;
  }
  return digits;
};

/** Máscara visível do telefone a partir dos dígitos nacionais. */
export const displayPixPhone = (stored: string): string => {
  if (!stored) return "";
  const local = nationalPhoneDigits(stored);
  const ddd = local.slice(0, 2);
  const number = local.slice(2, 11);
  if (!ddd) return "+55";
  let text = `+55 (${ddd}`;
  if (ddd.length < 2) return text;
  text += ")";
  if (!number) return text;
  if (number.length <= 4) return `${text} ${number}`;
  if (number.length === 8) {
    return `${text} ${number.slice(0, 4)}-${number.slice(4)}`;
  }
  if (number.length <= 5) return `${text} ${number}`;
  return `${text} ${number.slice(0, 5)}-${number.slice(5)}`;
};

/**
 * Formata um CPF para o padrão XXX.XXX.XXX-XX
 */
export const formatCPF = (cpf: string): string => {
  const cleanCPF = cpf.replace(/\D/g, "").slice(0, 11);
  return cleanCPF
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{2})$/, "$1-$2");
};

/**
 * Formata um telefone para o padrão (XX) 9XXXX-XXXX ou (XX) XXXX-XXXX
 */
export const formatPhone = (phone: string): string => {
  const cleanPhone = phone.replace(/\D/g, "");
  const phoneWithoutCountryCode = cleanPhone.startsWith("55")
    ? cleanPhone.substring(2)
    : cleanPhone;

  if (phoneWithoutCountryCode.length === 11) {
    return phoneWithoutCountryCode
      .replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  return phoneWithoutCountryCode
    .replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
};
